import { Decoder, Errors } from 'io-ts';
import { map, mapLeft } from 'fp-ts/lib/Either';
import { fromEither } from 'fp-ts/lib/TaskEither';
import { flow, pipe } from 'fp-ts/lib/function';
import { failure } from 'io-ts/lib/PathReporter';
import { log } from '@/services/logger';
import { ApiResponse } from './response';
import { Versioned } from '../versioned';
import { createdID } from './types';
import { ApiError } from './error';
import { DECODE_ERROR } from '../fetch';
import * as Sentry from '@sentry/browser';

export const logApiError = (error: ApiError) => {
  log(error);
  return error;
};

// TODO: this could indicate that the incoming data is not what we expected, so the client
//  (this app) should display an error letting the user know that the app is probably outdated
//  (that is the most likely reason why this might happen), and show a reload button that does
//  a full refresh
export const toApiError = (errors: Errors): ApiError => {
  //please leave this console here, joined errors impossible to debug!
  const stringErrors = failure(errors).join('\n');
  console.error(stringErrors);
  console.error(errors);
  Sentry.withScope(scope => {
    scope.setExtra('decodeErrors', JSON.stringify(errors));
    Sentry.captureException(stringErrors);
  });
  return new ApiError(DECODE_ERROR, stringErrors);
};

export const decode = <T>(decoder: Decoder<unknown, T>) => flow(
  (response: ApiResponse) => response.parsed,
  decoder.decode,
  mapLeft(toApiError),
  mapLeft(logApiError),
  fromEither,
);

export const decodeId = decode(createdID);

const extractVersion = (response: ApiResponse): string => response.headers.get('ETag') || '';

const wrapVersioned = (response: ApiResponse): Versioned<unknown> => ({
  data: response.parsed,
  version: extractVersion(response),
});

const decodeVersionedUnknown = <T>(
  decoder: Decoder<unknown, T>,
) => (versioned: Versioned<unknown>) => pipe(
  versioned.data,
  decoder.decode,
  mapLeft(toApiError),
  mapLeft(logApiError),
  map((decoded): Versioned<T> => ({
    data: decoded,
    version: versioned.version,
  })),
  fromEither,
);

export const decodeVersioned = <T>(decoder: Decoder<unknown, T>) => flow(
  wrapVersioned,
  decodeVersionedUnknown(decoder),
);
